import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import Loader from '../../components/common/Loader';
import BadgeStatus from '../../components/common/BadgeStatus';
import '../../styles/Forms.css';

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [asset, setAsset] = useState(null);
  const [handover, setHandover] = useState(null);
  const [allHandovers, setAllHandovers] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Get user role helper function
  const getUserRole = () => {
    try {
      const userStr = sessionStorage.getItem('user');
      if (!userStr) return 'admin';
      const user = JSON.parse(userStr);
      return user.role || 'admin';
    } catch (error) {
      return 'admin';
    }
  };

  const userRole = getUserRole();

  useEffect(() => {
    fetchAssetDetails();
  }, [id]);

  // Keyboard navigation for image lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;

      if (e.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (e.key === 'ArrowLeft' && asset?.photos && selectedImageIndex > 0) {
        setSelectedImageIndex(selectedImageIndex - 1);
      } else if (e.key === 'ArrowRight' && asset?.photos && selectedImageIndex < asset.photos.length - 1) {
        setSelectedImageIndex(selectedImageIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, asset]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch asset details
      const assetResponse = await api.getAsset(id);
      if (assetResponse.success) {
        setAsset(assetResponse.data);
        
        // Fetch all handovers for this asset
        const handoversResponse = await api.getHandovers({ 
          asset: id
        });
        
        if (handoversResponse.success && handoversResponse.data.length > 0) {
          // Filter handovers for this specific asset
          const assetHandovers = handoversResponse.data.filter(
            h => {
              const assetId = h.asset?._id || h.asset?.id || h.asset;
              return assetId === id || assetId === assetResponse.data._id;
            }
          );
          
          // Sort by date (most recent first)
          assetHandovers.sort((a, b) => {
            const dateA = new Date(a.assignedDate || a.createdAt);
            const dateB = new Date(b.assignedDate || b.createdAt);
            return dateB - dateA;
          });
          
          // If asset is in_transfer status, prioritize finding the pending transfer handover
          let currentHandover = null;
          if (assetResponse.data.status === 'in_transfer') {
            // Find the most recent pending transfer handover
            const pendingTransfer = assetHandovers.find(
              h => h.handoverType === 'transfer' && h.acknowledgementStatus === 'pending'
            );
            if (pendingTransfer) {
              currentHandover = pendingTransfer;
            } else if (assetHandovers.length > 0) {
              currentHandover = assetHandovers[0];
            }
          } else if (assetHandovers.length > 0) {
            currentHandover = assetHandovers[0];
          }
          
          if (currentHandover) {
            setHandover(currentHandover);
          }
          
          // Set previous history entries (skip the current one)
          // For employees: show only 1 previous, for admins: show 2 previous
          const currentIndex = currentHandover ? assetHandovers.indexOf(currentHandover) : -1;
          const maxPrevious = userRole === 'employee' ? 1 : 2;
          const previousHandovers = assetHandovers.filter((h, idx) => idx !== currentIndex).slice(0, maxPrevious);
          setAllHandovers(previousHandovers);
        }
      }
    } catch (error) {
      console.error('Error fetching asset details:', error);
      toast.error('Error loading asset details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status, issues) => {
    if (issues) return 'Issue Reported';
    if (status === 'acknowledged') return 'Received';
    if (status === 'pending') return 'pending';
    return status || 'N/A';
  };

  const getConditionLabel = (condition) => {
    if (!condition) return 'N/A';
    const conditionMap = {
      'excellent': 'Excellent',
      'good': 'Good',
      'fair': 'Fair',
      'poor': 'Poor',
      'damaged': 'Damaged',
      'repair_needed': 'Repair Needed'
    };
    return conditionMap[condition] || condition;
  };

  const getConditionBadgeStyle = (condition) => {
    if (!condition) return { bg: '#e2e8f0', text: '#4a5568' };
    
    const styleMap = {
      'excellent': { bg: '#c6f6d5', text: '#22543d' },
      'good': { bg: '#bee3f8', text: '#2c5282' },
      'fair': { bg: '#fefcbf', text: '#744210' },
      'poor': { bg: '#fed7aa', text: '#7c2d12' },
      'damaged': { bg: '#fed7d7', text: '#c53030' },
      'repair_needed': { bg: '#fbb6ce', text: '#97266d' }
    };
    
    return styleMap[condition] || { bg: '#e2e8f0', text: '#4a5568' };
  };

  if (loading) {
    return <Loader />;
  }

  if (!asset) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#718096', fontSize: '14px' }}>Asset not found</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: '16px',
            padding: '8px 16px',
            background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="asset-detail-page" style={{ padding: '16px' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(rgb(26, 32, 44) 0%, rgb(45, 55, 72) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '7px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '12px',
          }}
        >
          ← Back
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Left Column - Asset Info + Current Assignment */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* Asset Information Section */}
          <div>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Asset Information</h2>
            <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Asset Tag
              </label>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748' }}>
                {asset.assetTag}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Description
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.description}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Brand / Model / Serial No
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.brand || 'N/A'} {asset.model ? ` / ${asset.model}` : ''} {asset.serialNumber ? ` / ${asset.serialNumber}` : ''}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Category
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.category || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Status
              </label>
              <div>
                <BadgeStatus status={asset.status} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Condition
              </label>
              <div>
                {asset.condition ? (
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'inline-block',
                    backgroundColor: getConditionBadgeStyle(asset.condition).bg,
                    color: getConditionBadgeStyle(asset.condition).text
                  }}>
                    {getConditionLabel(asset.condition)}
                  </span>
                ) : (
                  <span style={{ fontSize: '14px', color: '#4a5568' }}>N/A</span>
                )}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Site & Location
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.location?.site?.name || 'N/A'} {asset.location?.locationDetail ? ` / ${asset.location.locationDetail}` : ''}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Department
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.location?.department?.name || 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Cost
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.cost ? `₹${parseFloat(asset.cost).toLocaleString('en-IN')}` : 'N/A'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                Purchase Date
              </label>
              <div style={{ fontSize: '14px', color: '#4a5568' }}>
                {asset.purchaseDate ? formatDate(asset.purchaseDate) : 'N/A'}
              </div>
            </div>
          </div>
          </div>

          {/* Current Assignment */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Current Assignment</h2>
            
            {handover ? (
                <div style={{
                  padding: '16px',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bfdbfe',
                  borderLeft: '4px solid #3b82f6'
                }}>
                  {(() => {
                    const isTransfer = handover.handoverType === 'transfer';
                    const currentHolder = isTransfer ? handover.assignedTo : (asset.assignedTo || handover.assignedTo);
                    
                    return (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '4px' }}>
                              {isTransfer ? 'Transfer' : 'Assignment'}
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '11px', 
                                padding: '2px 8px', 
                                background: '#3b82f6', 
                                color: 'white', 
                                borderRadius: '12px',
                                fontWeight: 500
                              }}>
                                Current
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096' }}>
                              {formatDate(handover.assignedDate || handover.createdAt)}
                            </div>
                          </div>
                          <BadgeStatus 
                            status={getStatusLabel(handover.acknowledgementStatus, handover.issues)}
                            variant={
                              getStatusLabel(handover.acknowledgementStatus, handover.issues) === 'Received' ? 'info' :
                              getStatusLabel(handover.acknowledgementStatus, handover.issues) === 'pending' ? 'warning' :
                              'danger'
                            }
                          />
                        </div>

                        <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                            Current Holder
                          </label>
                          <div style={{ fontSize: '16px', color: '#2d3748', fontWeight: 600 }}>
                            {currentHolder?.name || asset.assignedTo?.name || 'N/A'}
                          </div>
                          {(currentHolder?.email || asset.assignedTo?.email) && (
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                              {currentHolder?.email || asset.assignedTo?.email}
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                              {isTransfer ? 'Transferred To' : 'Assigned To'}
                            </label>
                            <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 500 }}>
                              {handover.assignedTo?.name || 'N/A'}
                            </div>
                            {handover.assignedTo?.email && (
                              <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                {handover.assignedTo.email}
                              </div>
                            )}
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                              {isTransfer ? 'Transferred By' : 'Assigned By'}
                            </label>
                            <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 500 }}>
                              {handover.assignedBy?.name || 'N/A'}
                            </div>
                            {handover.assignedBy?.email && (
                              <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                {handover.assignedBy.email}
                              </div>
                            )}
                          </div>
                        </div>

                        {handover.acknowledgedAt && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                              Acknowledged On
                            </label>
                            <div style={{ fontSize: '13px', color: '#4a5568' }}>
                              {formatDate(handover.acknowledgedAt)}
                            </div>
                          </div>
                        )}

                        {handover.notes && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                              Notes
                            </label>
                            <div style={{ fontSize: '13px', color: '#4a5568' }}>
                              {handover.notes}
                            </div>
                          </div>
                        )}

                        {handover.issues && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#c53030', marginBottom: '4px' }}>
                              Issue Reported
                            </label>
                            <div style={{ fontSize: '13px', color: '#c53030' }}>
                              {handover.issues}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : asset.assignedTo ? (
                <div style={{
                  padding: '16px',
                  background: '#fffaf0',
                  borderRadius: '6px',
                  border: '1px solid #fbd38d',
                  color: '#744210',
                  fontSize: '13px'
                }}>
                  ⚠️ No acknowledgement record found. Employee may need to confirm receipt.
                </div>
              ) : (
                <div style={{
                  padding: '16px',
                  background: '#f7fafc',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  color: '#718096',
                  fontSize: '13px',
                  textAlign: 'center'
                }}>
                  This asset is not currently assigned to anyone.
                </div>
              )
            }
          </div>
        </div>

        {/* Right Column - Photos & Transfer History */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* Asset Photos Section */}
          <div>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Asset Photos</h2>
            
            {asset.photos && asset.photos.length > 0 ? (
              <div>
                {/* Main Image Display */}
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <img
                    src={getImageUrl(asset.photos[selectedImageIndex !== null ? selectedImageIndex : 0].url)}
                    alt={asset.description}
                    onClick={() => setSelectedImageIndex(selectedImageIndex !== null ? selectedImageIndex : 0)}
                    style={{
                      width: '100%',
                      maxHeight: '350px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  />
                </div>
                
                {/* Thumbnail Gallery */}
                {asset.photos.length > 1 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
                    gap: '8px',
                    padding: '12px',
                    background: '#f7fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    {asset.photos.map((photo, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: selectedImageIndex === index ? '3px solid #667eea' : '2px solid #e2e8f0',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease',
                          opacity: selectedImageIndex === index ? 1 : 0.8
                        }}
                        onMouseEnter={(e) => {
                          if (selectedImageIndex !== index) {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedImageIndex !== index) {
                            e.currentTarget.style.opacity = '0.8';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <img
                          src={getImageUrl(photo.url)}
                          alt={`${asset.description} - Photo ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '70px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                        {selectedImageIndex === index && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'linear-gradient(to top, rgba(102, 126, 234, 0.8), transparent)',
                            padding: '4px',
                            fontSize: '10px',
                            color: 'white',
                            fontWeight: 600,
                            textAlign: 'center'
                          }}>
                            {index + 1}/{asset.photos.length}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Photo Count Badge */}
                <div style={{
                  marginTop: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#718096'
                }}>
                  {asset.photos.length} photo{asset.photos.length !== 1 ? 's' : ''} available
                </div>
              </div>
            ) : (
              <div style={{
                padding: '40px',
                background: '#f7fafc',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#a0aec0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>🖼️</div>
                <div style={{ fontSize: '13px' }}>No photos available</div>
              </div>
            )}
          </div>

          {/* Previous History (Last 2) */}
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 600 }}>
              Previous History{allHandovers.length > 0 && ` (${allHandovers.length})`}
            </h2>
            
            {allHandovers.length > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px'
              }}>
                {allHandovers.map((h, index) => {
                const isTransfer = h.handoverType === 'transfer';
                return (
                  <div
                    key={h._id || h.id || index}
                    style={{
                      padding: '12px',
                      background: '#f7fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #cbd5e0'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#2d3748', marginBottom: '4px' }}>
                          {isTransfer ? 'Transfer' : 'Assignment'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#718096' }}>
                          {formatDate(h.assignedDate || h.createdAt)}
                        </div>
                      </div>
                      <BadgeStatus 
                        status={getStatusLabel(h.acknowledgementStatus, h.issues)}
                        variant={
                          getStatusLabel(h.acknowledgementStatus, h.issues) === 'Received' ? 'info' :
                          getStatusLabel(h.acknowledgementStatus, h.issues) === 'pending' ? 'warning' :
                          'danger'
                        }
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                          {isTransfer ? 'Transferred To' : 'Assigned To'}
                        </label>
                        <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 500 }}>
                          {h.assignedTo?.name || 'N/A'}
                        </div>
                        {h.assignedTo?.email && (
                          <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                            {h.assignedTo.email}
                          </div>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                          {isTransfer ? 'Transferred By' : 'Assigned By'}
                        </label>
                        <div style={{ fontSize: '14px', color: '#2d3748', fontWeight: 500 }}>
                          {h.assignedBy?.name || 'N/A'}
                        </div>
                        {h.assignedBy?.email && (
                          <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                            {h.assignedBy.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {h.acknowledgedAt && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                          Acknowledged On
                        </label>
                        <div style={{ fontSize: '13px', color: '#4a5568' }}>
                          {formatDate(h.acknowledgedAt)}
                        </div>
                      </div>
                    )}

                    {h.notes && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                          Notes
                        </label>
                        <div style={{ fontSize: '13px', color: '#4a5568' }}>
                          {h.notes}
                        </div>
                      </div>
                    )}

                    {h.issues && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#c53030', marginBottom: '4px' }}>
                          Issue Reported
                        </label>
                        <div style={{ fontSize: '13px', color: '#c53030' }}>
                          {h.issues}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            ) : (
              <div style={{
                padding: '24px',
                background: '#f7fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                textAlign: 'center',
                color: '#718096',
                fontSize: '13px'
              }}>
                No previous history available.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {selectedImageIndex !== null && asset.photos && asset.photos.length > 0 && (
        <div
          onClick={() => setSelectedImageIndex(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedImageIndex(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>

            {/* Main Image */}
            <img
              src={getImageUrl(asset.photos[selectedImageIndex].url)}
              alt={`${asset.description} - Photo ${selectedImageIndex + 1}`}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
              }}
            />

            {/* Navigation Arrows */}
            {asset.photos.length > 1 && (
              <>
                {selectedImageIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex - 1);
                    }}
                    style={{
                      position: 'absolute',
                      left: '-50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ‹
                  </button>
                )}
                {selectedImageIndex < asset.photos.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex + 1);
                    }}
                    style={{
                      position: 'absolute',
                      right: '-50px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      fontSize: '20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                  >
                    ›
                  </button>
                )}
              </>
            )}

            {/* Image Counter */}
            <div style={{
              marginTop: '16px',
              color: 'white',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {selectedImageIndex + 1} / {asset.photos.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetDetailPage;
